import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import instance from "../apiInstance";
import { userLogin, resetState } from "../redux/authSlice";
import countryCodes from "../components/countries/Countries";
import "./Gate.css";

const validateEmail = (email) => {
  if (!email) return "Informe seu email";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Email inválido";
  return "";
};

// Hero da porta. Troque por sua foto: coloque em web-storie/public/gate-hero.jpg
// ou defina REACT_APP_GATE_HERO com a URL da imagem.
const HERO_IMG = process.env.REACT_APP_GATE_HERO || "/gate-hero.jpg";

function GateLogin({ onEntered }) {
  const [email, setEmail] = useState("");
  const [keepLogged, setKeepLogged] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(email.trim().toLowerCase());
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await instance.post(
        "/gate/enter",
        { email: email.trim().toLowerCase(), keep_logged_in: keepLogged },
        { headers: { noToken: true } }
      );
      onEntered(res.data.access_token, res.data.is_new);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não deu pra entrar. Tenta de novo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gate-portal">
      <div
        className="gate-portal-hero"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />

      <div className="gate-portal-body">
        <h1 className="gate-portal-title">Sua Personal Shopper</h1>
        <p className="gate-portal-sub">
          Receba seu convite para o grupo exclusivo
          <br />
          de promoções e novidades
        </p>

        <form className="gate-portal-form" onSubmit={submit}>
          <input
            className="gate-portal-input"
            type="email"
            autoComplete="email"
            placeholder="Seu melhor e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
          />
          {error && <p className="gate-portal-error">{error}</p>}
          <button className="gate-portal-cta" type="submit" disabled={loading}>
            {loading ? "Enviando seu convite…" : "Receber convite"}
          </button>
          <label className="gate-portal-keep">
            <input
              type="checkbox"
              checked={keepLogged}
              onChange={(e) => setKeepLogged(e.target.checked)}
            />
            <span>Manter acesso salvo · {keepLogged ? "30 dias" : "1 dia"}</span>
          </label>
        </form>

        <div className="gate-portal-brands">
          Dior • Chanel • Gucci • Louis Vuitton
        </div>
      </div>
    </div>
  );
}

function GateProfileForm({ sending, onSubmit }) {
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("+55");
  const [ddd, setDdd] = useState("");
  const [number, setNumber] = useState("");
  const [err, setErr] = useState("");
  const isBrazil = countryCode === "+55";

  const onCountry = (code) => {
    setCountryCode(code);
    if (code !== "+55") setDdd("");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr("Como posso te chamar?");
      return;
    }
    if (isBrazil && (!ddd || ddd.length < 2 || !number)) {
      setErr("Coloca DDD e número 💛");
      return;
    }
    if (!isBrazil && number.replace(/\D/g, "").length < 6) {
      setErr("Coloca seu número 💛");
      return;
    }
    setErr("");
    const digits = isBrazil ? `${ddd}${number}` : number;
    onSubmit({ name: name.trim(), phone: `${countryCode}${digits}` });
  };

  return (
    <form className="gate-formcard" onSubmit={submit}>
      <label className="gate-formlabel">Seu nome</label>
      <input
        className="gate-forminput"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Como posso te chamar?"
        autoComplete="given-name"
      />

      <label className="gate-formlabel">Seu WhatsApp</label>
      <div className={`gate-formphone${isBrazil ? "" : " is-intl"}`}>
        <select
          className="gate-formselect"
          value={countryCode}
          onChange={(e) => onCountry(e.target.value)}
          aria-label="País"
        >
          {Object.keys(countryCodes).map((code) => (
            <option key={code} value={code}>
              {countryCodes[code]} {code}
            </option>
          ))}
        </select>
        {isBrazil && (
          <input
            className="gate-formddd"
            inputMode="numeric"
            placeholder="DD"
            size={2}
            maxLength={2}
            value={ddd}
            onChange={(e) => setDdd(e.target.value.replace(/\D/g, "").slice(0, 2))}
          />
        )}
        <input
          className="gate-formnum"
          inputMode="numeric"
          placeholder={isBrazil ? "Seu número" : "Phone number"}
          value={number}
          onChange={(e) =>
            setNumber(e.target.value.replace(/\D/g, "").slice(0, isBrazil ? 9 : 15))
          }
        />
      </div>

      {err && <p className="gate-portal-error">{err}</p>}

      <button className="gate-cta" type="submit" disabled={sending}>
        {sending ? "Enviando…" : "Enviar e receber convite"}
      </button>
    </form>
  );
}

function Bubble({ msg }) {
  const mine = msg.role === "user";
  return (
    <div className={`gate-row ${mine ? "mine" : "theirs"}`}>
      <div className={`gate-bubble ${mine ? "mine" : "theirs"}`}>
        {msg.text}
      </div>
    </div>
  );
}

function GateChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [liveProfile, setLiveProfile] = useState({});
  const bottomRef = useRef(null);

  const scrollEnd = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    instance
      .get("/gate/thread")
      .then((res) => {
        setMessages(res.data.messages || []);
        setLiveProfile(res.data.profile || {});
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    scrollEnd();
  }, [messages, sending]);

  const send = useCallback(async ({ text, action, href, label, name, phone }) => {
    if (sending) return;
    setSending(true);
    try {
      const res = await instance.post("/gate/message", {
        text: text || "",
        action: action || "",
        label: label || "",
        name: name || "",
        phone: phone || "",
      });
      setMessages((prev) => [...prev, ...(res.data.messages || [])]);
      setLiveProfile(res.data.profile || {});
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { id: `err-${Date.now()}`, role: "agent", text: "Não rolou agora. Tenta de novo, amiga.", buttons: [] },
      ]);
    } finally {
      setSending(false);
    }
    if (!href) return;
    if (href.startsWith("/")) navigate(href);
    else window.open(href, "_blank", "noopener,noreferrer");
  }, [sending, navigate]);

  const onSubmit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    send({ text });
  };

  const lastAgent = [...messages].reverse().find((m) => m.role === "agent");
  const needsForm =
    !loading &&
    messages.length > 0 &&
    !liveProfile.first_name &&
    !liveProfile.joined_group &&
    (liveProfile.step === "ask_profile" || lastAgent?.form === "profile" || !liveProfile.step);
  const lastButtons = needsForm
    ? []
    : [...messages].reverse().find((m) => m.role === "agent" && m.buttons?.length)?.buttons || [];

  return (
    <div className="gate-chat">
      <header className="gate-top">
        <div className="gate-avatar" aria-hidden="true">PS</div>
        <div className="gate-top-meta">
          <strong>Personal Shopper</strong>
          <span>online · só você e ela</span>
        </div>
      </header>

      <div className="gate-live">
          <span>Perfil coletado</span>
          <em>{liveProfile.recap || "ainda vazio — cada resposta entra no CRM"}</em>
          <small>{liveProfile.answers || 0} resposta(s)</small>
        </div>

      <div className="gate-thread">
        {loading && <p className="gate-hint">Abrindo a conversa…</p>}
        {messages.map((msg) => (
          <Bubble key={msg.id} msg={msg} />
        ))}
        {sending && (
          <div className="gate-row theirs">
            <div className="gate-bubble theirs typing">digitando…</div>
          </div>
        )}
        {needsForm && (
          <div className="gate-actions gate-actions-inline">
            <GateProfileForm
              sending={sending}
              onSubmit={({ name, phone }) =>
                send({
                  action: "submit_profile",
                  name,
                  phone,
                  label: `${name} · ${phone}`,
                })
              }
            />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {lastButtons.length > 0 && (
        <div className="gate-actions">
          {lastButtons.map((btn) => (
            <button
              key={btn.id + (btn.label || "")}
              type="button"
              className="gate-cta"
              disabled={sending}
              onClick={() => send({ action: btn.id, href: btn.href, label: btn.label })}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}

      {!needsForm && (
      <form className="gate-composer" onSubmit={onSubmit}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escreve aqui…"
          autoComplete="off"
        />
        <button type="submit" disabled={sending || !input.trim()}>Enviar</button>
      </form>
      )}
    </div>
  );
}

export default function Gate() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const forceLogin =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("login") === "1";

  const onEntered = (accessToken) => {
    dispatch({ type: userLogin.fulfilled.type, payload: accessToken });
    if (forceLogin && typeof window !== "undefined") {
      window.history.replaceState({}, "", "/gate");
    }
  };

  if (forceLogin && token) {
    return (
      <GateLogin
        onEntered={onEntered}
        onReset={() => dispatch(resetState())}
      />
    );
  }

  if (!token) return <GateLogin onEntered={onEntered} />;
  return <GateChat />;
}
